/**
 * WordPress dependencies
 */
import { store, getContext } from '@wordpress/interactivity';

import { dateUtils } from '../../utils/fixtures';

/**
 * Internal dependencies
 */
import { fetchFixturesFromApi, fetchCompetitions } from './api/fixtures-api';
import {
  getCenterDate,
  getTodayDate,
  isDateWithinLimits,
  setCenterDate,
  updateDateNavigation,
  updateActiveDate,
} from './utils/date-navigation';
import { animateTransition, updateCompetitionDropdown } from './utils/dom-helpers';
import {
  addComputedPropertiesToFixture,
  transformApiResponse,
  hasLiveFixtures,
} from './utils/fixtures-transform';

/**
 * Format timestamp for display
 *
 * @param {number} timestamp - Unix timestamp
 * @param {string} locale    - Locale string
 * @return {string} Formatted date string
 */
function formatTimestamp(timestamp, locale = 'en-US') {
  const date = new Date(timestamp * 1000);
  const options = {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  };
  return date.toLocaleString(locale, options);
}

/**
 * Store user preferences in cookies for server-side rendering
 */
function storeUserPreferences() {
  try {
    // Store timezone (30 days)
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (userTimezone) {
      const maxAge = 60 * 60 * 24 * 30;
      document.cookie = `user_timezone=${encodeURIComponent(userTimezone)}; path=/; max-age=${maxAge}; SameSite=Lax`;
    }

    // Store current date in user's browser timezone (24 hours)
    const userDate = dateUtils.toISODate();
    if (userDate) {
      const maxAge = 60 * 60 * 24;
      document.cookie = `user_date=${encodeURIComponent(userDate)}; path=/; max-age=${maxAge}; SameSite=Lax`;
    }
  } catch (error) {
    // Ignore errors
  }
}

/**
 * Unwrap API response to get fixtures array and timestamp
 *
 * @param {Object} apiResponse - API response object
 * @return {Object} Object with fixtures array and timestamp
 */
function unwrapApiResponse(apiResponse) {
  return {
    fixtures: apiResponse.fixtures || [],
    timestamp: apiResponse.timestamp || null,
  };
}

/**
 * Load fixtures from API
 *
 * @param {string} selectedDate        - Selected date in YYYY-MM-DD format
 * @param {string} selectedCompetition - Selected competition ID
 * @param {Object} fixturesState       - Interactivity API state
 */
async function loadFixtures(selectedDate, selectedCompetition, fixturesState) {
  try {
    fixturesState.loading = true;

    // Fetch from API
    const { fixtures: apiResponse } = await fetchFixturesFromApi(selectedDate, selectedCompetition);
    const { fixtures: rawFixtures, timestamp } = unwrapApiResponse(apiResponse);

    // Transform API response to grouped competition data and flattened fixture map
    const transformedData = transformApiResponse(rawFixtures, fixturesState.matchBasePath);

    // Update state with single day object
    fixturesState.currentDay = {
      date: transformedData.date || selectedDate,
      date_display: transformedData.date_display,
      competitionGroups: transformedData.competitionGroups,
    };
    fixturesState.fixtures = transformedData.fixturesById;
    fixturesState.isEmpty = transformedData.competitionGroups.length === 0;

    // Update last updated timestamp
    if (timestamp) {
      fixturesState.lastUpdated = formatTimestamp(timestamp, fixturesState.locale);
      fixturesState.lastUpdatedTimestamp = timestamp;
    }

    // Check if we have live fixtures and start polling if needed
    const today = getTodayDate();
    if (selectedDate === today && hasLiveFixtures(transformedData.fixturesById)) {
      actions.startLivePolling();
    } else {
      actions.stopLivePolling();
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error loading fixtures:', error);
    fixturesState.currentDay = { date: '', date_display: '', competitionGroups: [] };
    fixturesState.fixtures = {};
    fixturesState.isEmpty = true;
    actions.stopLivePolling();
  } finally {
    fixturesState.loading = false;
  }
}

/**
 * Load all available competitions from API
 * Ensures competition filter has all competitions, not just those in current fixture data
 */
async function loadCompetitions() {
  try {
    const competitions = await fetchCompetitions();
    // Update competition dropdown options
    const { state } = store('big-bite-stats-perform/fixtures');
    state.competitions = competitions;

    // Update the select element if it exists
    updateCompetitionDropdown(competitions);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error loading competitions:', error);
    // Silently fail - will use competitions from initial state if available
  }
}

/**
 * Interactivity API Store
 */
const { state, actions } = store('big-bite-stats-perform/fixtures', {
  state: {
    showCalendar: false,
    isLivePollingActive: false,
    pollingIntervalId: null,
    // Derived state for checking if fixture is live
    get isFixtureLive() {
      const context = getContext();
      const status = context.fixture?.status || '';
      return status.toLowerCase() === 'live';
    },
  },
  actions: {
    /**
     * Start live update polling
     */
    startLivePolling: async () => {
      // Don't start if already polling (check flag immediately to prevent race conditions)
      if (state.isLivePollingActive) {
        return;
      }

      state.isLivePollingActive = true;

      // Calculate next 60-second boundary for synchronized polling
      const now = Date.now();
      const pollingInterval = 60000; // 60 seconds
      const nextBoundary = Math.ceil(now / pollingInterval) * pollingInterval;
      const delayToNextBoundary = nextBoundary - now;

      // Fire immediate poll to catch any updates since page load
      await actions.pollForLiveUpdates();

      // Schedule synchronized polling at 60-second boundaries
      setTimeout(() => {
        state.pollingIntervalId = setInterval(async () => {
          await actions.pollForLiveUpdates();
        }, pollingInterval);
      }, delayToNextBoundary);
    },

    /**
     * Stop live update polling
     */
    stopLivePolling: () => {
      if (state.pollingIntervalId) {
        clearInterval(state.pollingIntervalId);
        state.pollingIntervalId = null;
        state.isLivePollingActive = false;
      }
    },

    /**
     * Poll for live fixture updates
     */
    pollForLiveUpdates: async () => {
      // Only poll if we're on today's date
      if (state.selectedDate !== getTodayDate()) {
        actions.stopLivePolling();
        return;
      }

      try {
        // Fetch full fixture data with cache busting to bypass WPE object cache
        const { fixtures: apiResponse, hasLiveData } = await fetchFixturesFromApi(
          state.selectedDate,
          state.selectedCompetition,
          true, // Enable cache busting for polling requests
        );

        const { fixtures: rawFixtures, timestamp } = unwrapApiResponse(apiResponse);

        // Update timestamp display
        if (timestamp) {
          state.lastUpdated = formatTimestamp(timestamp, state.locale);
        }

        // Replace entire dataset with fresh data
        if (rawFixtures.length > 0) {
          const transformedData = transformApiResponse(rawFixtures, state.matchBasePath);

          state.currentDay = {
            date: transformedData.date || state.selectedDate,
            date_display: transformedData.date_display,
            competitionGroups: transformedData.competitionGroups,
          };
          state.fixtures = transformedData.fixturesById;
        }

        // Stop polling only if server explicitly says no live data
        if (!hasLiveData) {
          actions.stopLivePolling();
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Live update polling error:', error);
      }
    },

    /**
     * Handle competition change
     *
     * @param {Event} event Change event
     */
    changeCompetition: (event) => {
      state.selectedCompetition = event.target.value;
      loadFixtures(state.selectedDate, state.selectedCompetition, state);
    },

    /**
     * Navigate to previous week - shift the date range view backward
     */
    navigatePrevious: () => {
      const currentCenterDate = getCenterDate();
      const newCenterDate = dateUtils.toISODate(dateUtils.addDays(currentCenterDate, -7));

      // Limit horizontal navigation to 30 days either side of selected date
      if (!isDateWithinLimits(newCenterDate, state.selectedDate)) {
        return;
      }

      animateTransition(() => {
        setCenterDate(newCenterDate);
        updateDateNavigation(state.selectedDate, (date) => {
          state.selectedDate = date;
          updateActiveDate(date);
          loadFixtures(state.selectedDate, state.selectedCompetition, state);
        });
      });
    },

    /**
     * Navigate to next week - shift the date range view forward
     */
    navigateNext: () => {
      const currentCenterDate = getCenterDate();
      const newCenterDate = dateUtils.toISODate(dateUtils.addDays(currentCenterDate, 7));

      // Limit horizontal navigation to 30 days either side of selected date
      if (!isDateWithinLimits(newCenterDate, state.selectedDate)) {
        return;
      }

      animateTransition(() => {
        setCenterDate(newCenterDate);
        updateDateNavigation(state.selectedDate, (date) => {
          state.selectedDate = date;
          updateActiveDate(date);
          loadFixtures(state.selectedDate, state.selectedCompetition, state);
        });
      });
    },

    /**
     * Navigate to today - reset center date and selected date to today
     */
    navigateToday: () => {
      const todayDate = dateUtils.toISODate();

      animateTransition(() => {
        setCenterDate(todayDate);
        state.selectedDate = todayDate;
        // Update navigation with callback for date button clicks
        updateDateNavigation(state.selectedDate, (date) => {
          state.selectedDate = date;
          updateActiveDate(date);
          loadFixtures(state.selectedDate, state.selectedCompetition, state);
        });
      });
      // Load fixtures for today's date (outside animation to avoid double call from callback)
      loadFixtures(todayDate, state.selectedCompetition, state);
    },

    /**
     * Toggle calendar display
     */
    toggleCalendar: () => {
      state.showCalendar = !state.showCalendar;
    },

    /**
     * Handle date selection from calendar
     * @param {Event} event - Calendar change event
     */
    selectCalendarDate: (event) => {
      const selectedDate = event.target.value;
      if (selectedDate) {
        state.selectedDate = selectedDate;
        state.showCalendar = false;

        // Update center date to keep selected date in view
        setCenterDate(selectedDate);
        // Update navigation with callback for date button clicks
        updateDateNavigation(selectedDate, (date) => {
          state.selectedDate = date;
          updateActiveDate(date);
          loadFixtures(state.selectedDate, state.selectedCompetition, state);
        });
        // Load fixtures for selected date
        loadFixtures(selectedDate, state.selectedCompetition, state);
      }
    },

    /**
     * Navigate to match page when fixture is clicked
     *
     * @param {Event} event - Click event
     */
    navigateToMatch: (event) => {
      const fixtureElement = event.currentTarget;
      const matchUrl = fixtureElement.getAttribute('data-match-url');

      if (matchUrl) {
        window.location.href = matchUrl;
      }
    },
  },
  callbacks: {
    /**
     * Initialize the block
     */
    init: () => {
      // Store user preferences (timezone and date) in cookies
      storeUserPreferences();

      // Format initial "Last updated" timestamp if not already formatted server-side
      if (state.lastUpdatedTimestamp && !state.lastUpdated) {
        state.lastUpdated = formatTimestamp(state.lastUpdatedTimestamp, state.locale);
      }

      // Convert initial fixture times from UTC to user's local timezone
      // This handles first visit before cookie is set
      if (state.currentDay?.competitionGroups) {
        state.currentDay.competitionGroups.forEach((group) => {
          group.fixtures.forEach((fixture) => {
            const converted = addComputedPropertiesToFixture(fixture, state.matchBasePath);
            // Update the fixture with converted time
            Object.assign(fixture, converted);
            // Also update in flattened fixtures map
            if (fixture.id && state.fixtures[fixture.id]) {
              state.fixtures[fixture.id] = converted;
            }
          });
        });
      }

      // Initialize date navigation
      updateDateNavigation(state.selectedDate, (date) => {
        state.selectedDate = date;
        updateActiveDate(date);
        loadFixtures(state.selectedDate, state.selectedCompetition, state);
      });

      // Load all available competitions
      loadCompetitions();

      // Check if we should start polling for live fixtures
      if (state.currentDay?.competitionGroups.length > 0) {
        const today = getTodayDate();
        if (state.selectedDate === today && hasLiveFixtures(state.fixtures)) {
          actions.startLivePolling();
        }
      } else if (!state.currentDay) {
        loadFixtures(state.selectedDate, state.selectedCompetition, state);
      }

      // Clean up polling on page unload
      window.addEventListener('beforeunload', actions.stopLivePolling);

      // Handle page restoration from bfcache (browser back/forward)
      window.addEventListener('pageshow', (event) => {
        // event.persisted is true when page is restored from bfcache
        if (event.persisted) {
          const today = getTodayDate();
          // Reload fixtures and restart polling if on today's date
          if (state.selectedDate === today) {
            loadFixtures(state.selectedDate, state.selectedCompetition, state);
          }
        }
      });
    },
  },
});
